import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router";
import { useHistory } from "react-router-dom";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button ";
import DebtorNameHeader from "@shared/components/DebtorNameHeader";
import { printReceiptHandler } from "@shared/functions/print";
import { safePrint } from "../functions/safePrint";
import { logger } from "../functions/logger";
import LoanDetailMenu from "@shared/components/LoanDetailMenu";
import PaymentNotFound from "../components/PaymentNotFound";
import { useSelector } from "react-redux";
import RenderBackButton from "@shared/components/RenderBackButton";
import { selectCommon } from "../store/slices/common";
import { selectPaymentById } from "../store/slices/payments";
import { selectLoanById } from "../store/slices/loans";
import { useGetOwnerConfigQuery, useGetUserConfigQuery, useGetRouteQuery, useGetUserQuery } from "../store/slices/api";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import PrintIcon from "@material-ui/icons/Print";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import parse from "@nodes/html-react-parser";
// import Loading from "@shared/components/Loading";
import { makeStyles } from "@nodes/@material-ui/core/styles";
// import { selectSyncronizationById } from "../store/slices/syncronization";

const useStyles = makeStyles({
  orange: {
    color: "orange",
  },
  red: {
    color: "red",
  },
  green: {
    color: "green",
  },
});

const PaymentReceipt = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id: payment_id, id2: syncronization_id, print: autoPrint } = useParams();
  const payment = useSelector((state) => selectPaymentById(state, payment_id));
  const paymentsCount = useSelector((state) => state.payments.payments.length);
  const loansCount = useSelector((state) => state.loans.loans.length);
  const paymentsCuadreId = useSelector((state) => state.payments.cuadre_id);
  // const sync = useSelector((state) => selectSyncronizationById(state, syncronization_id));
  const loan = useSelector((state) => selectLoanById(state, payment?.money_id));
  const common = useSelector(selectCommon);
  const [receipt, setReceipt] = useState("");
  // Auto-print must fire once even though the effect now re-runs when the route
  // query settles, otherwise a slow /route/info prints the receipt twice.
  const autoPrinted = useRef(false);

  const { data: route, isLoading: routeLoading } = useGetRouteQuery({
    creditor_id: common.creditor_id,
    user_id: common.user_id,
    collect_date: common.collect_date,
  });
  const { data: ownerConfig } = useGetOwnerConfigQuery(common.owner_id);
  const { data: userConfig } = useGetUserConfigQuery(common.user_id);
  const { data: user } = useGetUserQuery(common.user_id);

  // `route` / `ownerConfig` / `userConfig` / `user` come from RTK Query, so they
  // are undefined until the request resolves — and they stay undefined when the
  // cache is cold (fresh install, storage eviction) and the device is offline.
  // Spreading undefined is harmless, but reading `route.phone` threw here and
  // tripped the ErrorBoundary on the page the collector lands on IMMEDIATELY
  // after applying a payment, which is reported as "the app logged me out".
  // Wait for the route query to settle (it fails fast offline, so this cannot
  // hang), then read it defensively.
  useEffect(() => {
    // if (!payment || (!sync && syncronization_id !== "undefined")) return;
    if (!payment || routeLoading) return;
    // localStorage.removeItem("lastPaymentId");
    setReceipt(
      safePrint(
        "print.receipt",
        printReceiptHandler,
        { ...loan, ...payment },
        {
          ...route,
          ...ownerConfig,
          ...userConfig,
          ...common,
          ...user,
          printApp: "html",
          excludeExtraInfo: true,
          routePhone: route?.phone,
          routePhone2: route?.phone2,
        }
      )
    );

    if (autoPrint === "1" && !autoPrinted.current) {
      autoPrinted.current = true;
      safePrint(
        "print.receipt",
        printReceiptHandler,
        { ...loan, ...payment },
        {
          ...route,
          ...ownerConfig,
          ...userConfig,
          ...common,
          ...user,
          routePhone: route?.phone,
          routePhone2: route?.phone2,
        }
      );
      history.replace("/dashboard");
    }
  }, [payment, route, routeLoading]);

  // The "Pago no fue guardado!" screen is what the collector sees when the redux
  // lists are empty (store wiped / IndexedDB evicted) right after applying a
  // payment. It left no trace at all, so the report could never be reproduced.
  // Record the state that produced it.
  useEffect(() => {
    if (payment && loan) return;

    logger.warning("receipt.payment_not_found", "Receipt page could not find the payment/loan", {
      entity: "payments",
      entityId: payment_id,
      context: {
        missing: !payment ? "payment" : "loan",
        payment_id,
        syncronization_id,
        money_id: payment?.money_id ?? null,
        payments_count: paymentsCount,
        loans_count: loansCount,
        payments_cuadre_id: paymentsCuadreId,
        common_cuadre_id: common?.cuadre_id ?? null,
        has_common: !!common?.creditor_id,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment, loan]);

  // Guard the render: after an IndexedDB eviction the payments/loans redux lists
  // can be momentarily empty, so selectPaymentById / selectLoanById return
  // undefined. Rendering on with an undefined `loan` crashed DebtorNameHeader
  // ("undefined.name") and tripped the ErrorBoundary. Show the not-found fallback
  // instead; the App-level recovery repopulates the lists and a re-nav recovers.
  if (!payment || !loan) return <PaymentNotFound />;

  const handleWhatsAppPrint = () => {
    handlePrinting(payment, { printDestination: "whatsapp" });
  };

  const handlePaymentPrint = () => {
    handlePrinting(payment, { printDestination: "printer" });
  };

  const handlePrinting = (payment, option) => {
    if (option.printDestination === "whatsapp") {
      window.location = `whatsapp://send?text=${safePrint(
        "print.receipt",
        printReceiptHandler,
        { ...loan, ...payment },
        {
          ...route,
          ...ownerConfig,
          ...common,
          printApp: "whatsapp",
          routePhone: route?.phone,
          routePhone2: route?.phone2,
        }
      )}`;
    } else {
      safePrint(
        "print.receipt",
        printReceiptHandler,
        { ...loan, ...payment },
        { ...route, ...ownerConfig, ...userConfig, ...common, routePhone: route?.phone, routePhone2: route?.phone2 }
      );
    }
  };

  return (
    <div className="mb-5">
      <DebtorNameHeader debtor={loan}>
        <LoanDetailMenu loan={loan} />
      </DebtorNameHeader>

      <Box component="div" p={1}>
        <div style={{ borderStyle: "ridge", borderWidth: 4 }}>
          <div style={{ padding: 15, paddingRight: 25 }}>{parse(receipt)}</div>
        </div>

        <Box paddingTop={2}>
          <Button
            fullWidth
            startIcon={<PrintIcon size="1.3em" />}
            variant="outlined"
            size="small"
            color="primary"
            onClick={handlePaymentPrint}>
            Imprimir Recibo
          </Button>
        </Box>

        <Box paddingTop={2}>
          <Button
            fullWidth
            classes={{ root: classes.green }}
            startIcon={<WhatsAppIcon size="1.3em" />}
            variant="outlined"
            color="primary"
            size="small"
            onClick={handleWhatsAppPrint}>
            Enviar WhatsApp
          </Button>
        </Box>

        <Box paddingTop={2}>
          <Button
            fullWidth
            disabled
            classes={{ root: classes.orange }}
            startIcon={<WhatsAppIcon size="1.3em" />}
            variant="outlined"
            color="primary"
            size="small">
            Modificar Pago
          </Button>
        </Box>

        <Box paddingTop={2} paddingBottom={2}>
          <Button
            fullWidth
            size="small"
            classes={{ root: classes.red }}
            startIcon={<DeleteForeverIcon size="1.3em" />}
            variant="outlined"
            color="default"
            onClick={() => history.push(`/delete/payment/${payment.payment_id}`)}>
            Borrar Pago
          </Button>
        </Box>

        <div className="mt-3">
          <RenderBackButton />
        </div>
      </Box>
    </div>
  );
};

export default PaymentReceipt;
